import { useReducer, useEffect, useCallback, useMemo, useContext } from 'react';
import isEqual from 'lodash.isequal';
import { useRouter } from 'next/router';

import { getValueFromEvent } from './utils';
import { useSearchHistory } from './history';
import { useTracking } from '@spa/shared/hooks/tracking';
import { cleanFields, cleanPage } from '@spa/shared/utils/queryParams';
import { ProfileContext } from '@spa/shared/contexts/profile';
import { useCityLocation } from '@spa/shared/hooks/search/location';
import { setQueryParamsFromObject } from '@spa/shared/utils/history/index';
import { trackAmplitude } from '../tracking/services/amplitude/dataAmplitude';

const SET_PARAMS = 'set_params';
const ADD_PARAMS = 'add_params';
const CLEAR_PARAMS = 'clear_params';

export const QUERY_PARAMS_KEYS = {
  ORDER: 'order',
  PAGE: 'page'
};

const paramsReducer = (state = {}, { type, payload }) => {
  switch (type) {
    case SET_PARAMS:
      return payload;
    case ADD_PARAMS: {
      return { ...state, ...payload };
    }
    case CLEAR_PARAMS:
      return {};
    default:
      state;
  }
};

export const useFilterToQueryParameters = ({ submitOnChange = true, onChange = params => params } = {}) => {
  const history = useRouter();
  const { query, changedParams } = useSearchHistory();
  const [params, dispatch] = useReducer(paramsReducer, {});
  const { track, events, experimentId } = useTracking();
  const { location: { configuration: { slug, country_code } = {} } = {} } = useCityLocation();
  const { profile } = useContext(ProfileContext);

  // external changes to query
  useEffect(() => dispatch({ type: CLEAR_PARAMS }), [query]);

  // submit the changes handler
  useEffect(() => {
    if (!submitOnChange) {
      return;
    }

    submitHandler();
  }, [params]);

  const fields = useMemo(() => {
    return cleanFields(submitOnChange ? query : { ...query, ...params });
  }, [submitOnChange, query, params]);

  const submitHandler = useCallback(() => {
    if (params && !Object.keys(params).length) {
      return;
    }

    const { page, ...restParams } = params;
    const newQuery = { ...cleanPage(query), ...restParams, ...(page ? { page } : {}) };
    if (isEqual(query, newQuery)) {
      return;
    }

    setQueryParamsFromObject(history, newQuery);
  }, [history, query, params]);

  // handlers to export
  const clearFieldsHandler = useCallback(() => {
    const payload = Object.keys(query).reduce((acc, key) => {
      if (key === QUERY_PARAMS_KEYS.ORDER) {
        return { ...acc, [key]: query[key] };
      }

      return { ...acc, [key]: undefined };
    }, {});

    dispatch({ type: SET_PARAMS, payload });
  }, [query]);

  const onMultiChangeHandler = useCallback(
    payload => {
      const eventParams = {
        ...query,
        ...payload,
        cityName: slug,
        userId: profile && profile.id,
        country: country_code,
        experiment_id: experimentId
      };

      track(events.filtersApplied, { ...eventParams }, { query, payload });
      trackAmplitude(events.filtersApplied, { ...payload });

      dispatch({ type: ADD_PARAMS, payload: onChange(payload) });
    },
    [changedParams]
  );

  const onChangeHandler = useCallback(
    (key, value, forceArray = false) => {
      const isArrayForced = forceArray && value && !(value instanceof Array);
      const payload = isArrayForced ? { [key]: [value] } : { [key]: value };

      onMultiChangeHandler(payload);
    },
    [onMultiChangeHandler]
  );

  const onChangeHandlerForceArray = useCallback(
    (key, value) => {
      onChangeHandler(key, value, true);
    },
    [onMultiChangeHandler]
  );

  const onChangeEventHandler = useCallback(
    (event, isValueBinary = false) => {
      const {
        target: { name: key }
      } = event;

      onChangeHandler(key, getValueFromEvent(event, isValueBinary));
    },
    [onChangeHandler]
  );

  const onChangeEventForceArrayHandler = useCallback(
    event => {
      const {
        target: { name: key }
      } = event;

      onChangeHandler(key, getValueFromEvent(event), true);
    },
    [onChangeHandler]
  );

  return {
    fields,
    query,
    onChangeHandler,
    onChangeEventHandler,
    onChangeEventForceArrayHandler,
    onChangeHandlerForceArray,
    onMultiChangeHandler,
    clearFieldsHandler,
    submitHandler
  };
};
